import { ArrowDown, ArrowUp, HelpCircle } from "lucide-react";

import { ErrorMessage } from "@/app/components/error-message";
import { Card } from "@/components/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetRoomSystemBaseAnalytics } from "../api/get-room-system-base-analytics";

type RoomSystemBaseAnalyticsProps = {
  roomSystemId?: string;
  organisationId?: string;
};

export function RoomSystemBaseAnalytics({
  roomSystemId,
  organisationId,
}: RoomSystemBaseAnalyticsProps) {
  const { data, isLoading, isSuccess, isError, error, isFetching } =
    useGetRoomSystemBaseAnalytics(organisationId, roomSystemId);

  if (isLoading || isFetching) {
    return (
      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="[&>div]:h-full">
          <Card.Content className="flex flex-1 flex-col">
            <p className="mb-5 text-sm font-semibold text-foreground">
              Average Session Duration
            </p>

            <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
              <p className="text-xl font-bold">
                <Skeleton className="h-6 w-20 text-muted-foreground/80" />
              </p>
            </div>
          </Card.Content>
        </Card>
        <Card className="[&>div]:h-full">
          <Card.Content className="flex flex-1 flex-col">
            <p className="text-sm font-semibold text-foreground">
              Sessions Scheduled
            </p>
            <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
              <p className="text-xl font-bold">
                <Skeleton className="h-6 w-20 text-muted-foreground/80" />
              </p>
            </div>
          </Card.Content>
        </Card>
        <Card className="[&>div]:h-full">
          <Card.Content className="flex flex-1 flex-col">
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <p className="text-sm font-semibold text-foreground">
                  Collaborative Sessions
                  <TooltipTrigger asChild>
                    <HelpCircle className="ml-2 inline-block size-4 cursor-pointer text-muted-foreground" />
                  </TooltipTrigger>
                </p>
                <TooltipContent className="max-w-xs">
                  <span>
                    A collaborating organisation is when more than one
                    organisation takes part in a session.
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
              <p className="text-xl font-bold">
                <Skeleton className="h-6 w-20 text-muted-foreground/80" />
              </p>
            </div>
          </Card.Content>
        </Card>
      </section>
    );
  }

  if (isError || !isSuccess) {
    return <ErrorMessage error={error} />;
  }

  const { avgSessionDuration, collaboration, sessionCount } = data;
  const isPositiveAvgDuration =
    avgSessionDuration.current > avgSessionDuration.previous;
  const avgDurationChange = avgSessionDuration.previous
    ? Math.round(
        ((avgSessionDuration.current - avgSessionDuration.previous) /
          avgSessionDuration.previous) *
          100,
      )
    : 0;

  const isPositiveCollaboration =
    collaboration.current > collaboration.previous;
  const collaborationChange = collaboration.previous
    ? Math.round(
        ((collaboration.current - collaboration.previous) /
          collaboration.previous) *
          100,
      )
    : 0;

  const isPositiveSessionCount = sessionCount.current > sessionCount.previous;
  const sessionCountChange = sessionCount.previous
    ? Math.round(
        ((sessionCount.current - sessionCount.previous) /
          sessionCount.previous) *
          100,
      )
    : 0;

  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <Card className="[&>div]:h-full">
        <Card.Content className="flex flex-1 flex-col">
          <p className="mb-5 text-sm font-semibold text-foreground">
            Average Session Duration
          </p>

          <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
            <p className="text-xl font-bold text-foreground">
              {avgSessionDuration.current ?? 0} minutes
            </p>
            <div className="flex flex-col items-center xl:items-end">
              <Badge
                variant={isPositiveAvgDuration ? "success" : "destructive"}
              >
                {isPositiveAvgDuration ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
                <span>
                  {isPositiveAvgDuration ? "+" : ""}
                  {avgDurationChange}%
                </span>
              </Badge>
              <span className="text-right text-sm text-muted-foreground">
                vs last month
              </span>
            </div>
          </div>
        </Card.Content>
      </Card>
      <Card className="[&>div]:h-full">
        <Card.Content className="flex flex-1 flex-col">
          <p className="text-sm font-semibold text-foreground">
            Sessions Scheduled
          </p>
          <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
            <p className="text-xl font-bold text-foreground">
              {sessionCount.current}
            </p>
            <div className="flex flex-col items-center xl:items-end">
              <Badge
                variant={isPositiveSessionCount ? "success" : "destructive"}
              >
                {isPositiveSessionCount ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
                <span>
                  {isPositiveSessionCount ? "+" : ""}
                  {sessionCountChange}%
                </span>
              </Badge>
              <span className="text-right text-sm text-muted-foreground">
                vs last month
              </span>
            </div>
          </div>
        </Card.Content>
      </Card>
      <Card className="[&>div]:h-full">
        <Card.Content className="flex flex-1 flex-col">
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <p className="text-sm font-semibold text-foreground">
                Collaborative Sessions
                <TooltipTrigger asChild>
                  <HelpCircle className="ml-2 inline-block size-4 cursor-pointer text-muted-foreground" />
                </TooltipTrigger>
              </p>
              <TooltipContent className="max-w-xs">
                <span>
                  A collaborating organisation is when more than one
                  organisation takes part in a session.
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="mt-auto flex items-center justify-between sm:flex-col xl:flex-row">
            <p className="text-xl font-bold text-foreground">
              {collaboration.current}%
            </p>
            <div className="flex flex-col items-center xl:items-end">
              <Badge
                variant={isPositiveCollaboration ? "success" : "destructive"}
              >
                {isPositiveCollaboration ? (
                  <ArrowUp className="h-4 w-4" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
                <span>
                  {isPositiveCollaboration ? "+" : ""}
                  {collaborationChange}%
                </span>
              </Badge>
              <span className="text-right text-sm text-muted-foreground">
                vs last month
              </span>
            </div>
          </div>
        </Card.Content>
      </Card>
    </section>
  );
}
